import { Collider, ColliderShape } from '../components/Collider';
import { SpriteRenderer } from '../components/SpriteRenderer';
import { type EngineContext } from '../core/EngineContext';
import { SystemStage, type System } from '../core/System';
import { type DebugDraw } from '../debug/DebugDraw';
import { type DebugService } from '../debug/DebugService';
import { WorldTransform } from '../entities/WorldTransform';
import { Color } from '../math/Color';
import { rotatedX, rotatedY } from '../math/MathUtils';
import { fillSpriteQuad, type SpriteQuad } from '../rendering/SpriteQuad';
import { type Scene } from '../scene/Scene';
import { createWorldColliderShape, fillWorldCollider } from './ColliderGeometry';

/** Outline color for the quad a sprite occupies. */
const SPRITE_BOUNDS_COLOR = Color.fromHex('#4cc9f0cc');

/** Outline color for the shape a collider tests with. */
const COLLIDER_BOUNDS_COLOR = Color.fromHex('#ff6b6bcc');

const CENTER_ANCHOR = 0.5;

/**
 * The last pass of the frame: measure it, outline it, then hand it to whoever is watching.
 *
 * A system rather than a behavior because none of this belongs to an entity: frame time is the whole
 * frame's, and bounds are every sprite's. It runs after render sync so the outlines describe the frame
 * that was just drawn rather than the one before it.
 *
 * The shape list is cleared here, immediately after {@link DebugService.publishFrame} has offered it,
 * which is what makes a debug draw call from a behavior last exactly one frame.
 */
export class DebugSystem implements System {
  /** Debug output is produced last, once everything else has finished moving and drawing. */
  readonly stage = SystemStage.Debug;

  readonly #worldTransform = new WorldTransform();
  readonly #colliderShape = createWorldColliderShape();
  readonly #quad: SpriteQuad = {
    x: 0,
    y: 0,
    rotation: 0,
    width: 0,
    height: 0,
    anchorX: CENTER_ANCHOR,
    anchorY: CENTER_ANCHOR,
  };

  #debug: DebugService | undefined;

  /** Captures the debug service the frame's output is collected on. */
  onRegister(context: EngineContext): void {
    this.#debug = context.debug;
  }

  /** Samples the frame, outlines the scene when asked to, publishes and clears. */
  onUpdate(scene: Scene, deltaSeconds: number): void {
    const debug = this.#debug;
    if (debug === undefined) {
      return;
    }
    if (!debug.enabled) {
      debug.draw.clear();
      return;
    }

    debug.stats.sample(scene, deltaSeconds);
    if (debug.drawBounds) {
      this.#drawSpriteBounds(scene, debug.draw);
      this.#drawColliderBounds(scene, debug.draw);
    }

    debug.publishFrame(scene);
    debug.draw.clear();
  }

  /** Drops the service, so a re-registered system cannot write into a disposed engine. */
  onUnregister(): void {
    this.#debug = undefined;
  }

  /*
   * The quad is placed by its anchor, so the outline's center is the anchor plus the offset from it to
   * the middle, rotated the way the sprite is. Reusing the sprite quad rather than recomputing the
   * corners keeps the outline exactly where the renderer put the art.
   */
  #drawSpriteBounds(scene: Scene, draw: DebugDraw): void {
    for (const entity of scene.getEntitiesWith(SpriteRenderer)) {
      const sprite = entity.getComponent(SpriteRenderer);
      if (sprite === undefined || !sprite.visible || !entity.activeInHierarchy) {
        continue;
      }

      entity.getWorldTransform(this.#worldTransform);
      const quad = fillSpriteQuad(this.#quad, sprite, this.#worldTransform);
      const offsetX = (CENTER_ANCHOR - quad.anchorX) * quad.width;
      const offsetY = (CENTER_ANCHOR - quad.anchorY) * quad.height;
      const cos = Math.cos(quad.rotation);
      const sin = Math.sin(quad.rotation);

      draw.rect(
        quad.x + rotatedX(offsetX, offsetY, cos, sin),
        quad.y + rotatedY(offsetX, offsetY, cos, sin),
        quad.width,
        quad.height,
        SPRITE_BOUNDS_COLOR,
        quad.rotation,
      );
    }
  }

  #drawColliderBounds(scene: Scene, draw: DebugDraw): void {
    for (const entity of scene.getEntitiesWith(Collider)) {
      const collider = entity.getComponent(Collider);
      if (collider === undefined || !collider.enabled || !entity.activeInHierarchy) {
        continue;
      }

      entity.getWorldTransform(this.#worldTransform);
      const shape = fillWorldCollider(this.#colliderShape, this.#worldTransform, collider);

      if (shape.shape === ColliderShape.Circle) {
        draw.circle(shape.x, shape.y, shape.radius, COLLIDER_BOUNDS_COLOR);
      } else {
        draw.rect(shape.x, shape.y, shape.bounds.width, shape.bounds.height, COLLIDER_BOUNDS_COLOR);
      }
    }
  }
}
